export type Citation = {
  title: string;
  journal: string;
  year: number;
  url: string; // PubMed / DOI
};

export type IngredientResearch = {
  slug: string;
  name: string;
  sku: "mag" | "liver" | "ova";
  skuLabel: string;
  scientist: string;
  affiliation: string;
  summary: string;
  studyCount: string;
  pdf: string; // /research/{slug}.pdf
  webmd?: string; // consumer-facing WebMD reference
  clinicalTrials?: string; // ClinicalTrials.gov search/registry URL
  medrxiv?: string; // medRxiv preprint search URL
  biorxiv?: string; // bioRxiv preprint search URL
  citations: Citation[];
};


// Every PMID below has been verified against PubMed eutils — title, journal
// and year match the cited claim. Do not swap PMIDs without re-verifying via
// https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=pubmed&id=<pmid>
export const RESEARCH: IngredientResearch[] = [
  {
    slug: "vitamin-d3",
    name: "Vitamin D3 (Cholecalciferol)",
    sku: "ova",
    skuLabel: "Ova Balance",
    scientist: "H. M. Trang et al.",
    affiliation: "University of Toronto and The Wellesley Hospital, Canada",
    summary: "Ova Balance provides 600 IU (15 mcg) of vitamin D3 per sachet. Vitamin D supports calcium absorption and normal bone, muscle and immune function. The cited trial studied vitamin D status at a different dose; it does not establish finished-product PCOS or fertility benefits.",
    studyCount: "2 cited sources",
    pdf: "/research/vitamin-d3.pdf",
    citations: [
      { title: "Vitamin D: Fact Sheet for Health Professionals", journal: "NIH Office of Dietary Supplements", year: 2025, url: "https://ods.od.nih.gov/factsheets/VitaminD-HealthProfessional/" },
      { title: "Evidence that vitamin D3 increases serum 25-hydroxyvitamin D more efficiently than does vitamin D2", journal: "The American Journal of Clinical Nutrition", year: 1998, url: "https://pubmed.ncbi.nlm.nih.gov/9771862/" },
    ],
  },
  {
    slug: "aquamin-marine-magnesium",
    name: "Aquamin® Marine Magnesium",
    sku: "mag",
    skuLabel: "Mag Absorb Pro",
    scientist: "Dr. Denise O'Gorman",
    affiliation: "Marigot Ltd / Aquamin Research",
    summary:
      "Multi-mineral magnesium from red marine algae (Lithothamnion sp.), supports sustained mineral delivery for joint comfort, bone structure and recovery.",
    studyCount: "30+ studies",
    pdf: "/research/magnesium-bisglycinate.pdf",
    webmd: "https://www.webmd.com/diet/supplement-guide-magnesium",
    clinicalTrials: "https://clinicaltrials.gov/search?term=Aquamin%20magnesium",
    medrxiv: "https://www.medrxiv.org/search/Aquamin%252Bmagnesium",
    biorxiv: "https://www.biorxiv.org/search/Aquamin%252Bmagnesium",
    citations: [
      { title: "A natural seaweed-derived mineral supplement (Aquamin F) for knee osteoarthritis: a randomised, placebo-controlled trial", journal: "Nutrition Journal", year: 2009, url: "https://pubmed.ncbi.nlm.nih.gov/19187557/" },
      { title: "The effect of magnesium supplementation on muscle fitness: a meta-analysis and systematic review", journal: "Magnesium Research", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/29637897/" },
      { title: "A mineral-rich extract from the red marine algae Lithothamnion calcareum preserves bone structure", journal: "Calcified Tissue International", year: 2010, url: "https://pubmed.ncbi.nlm.nih.gov/20180099/" },
    ],
  },
  {
    slug: "magnesium-bisglycinate",
    name: "Magnesium Bisglycinate",
    sku: "mag",
    skuLabel: "Mag Absorb Pro",
    scientist: "Dr. Stella Lucia Volpe",
    affiliation: "Virginia Tech",
    summary:
      "Highly bioavailable, gentle-on-gut chelate, the gold standard for relaxation, sleep onset and stress recovery.",
    studyCount: "300+ studies",
    pdf: "/research/magnesium-bisglycinate.pdf",
    webmd: "https://www.webmd.com/diet/supplement-guide-magnesium",
    clinicalTrials: "https://clinicaltrials.gov/search?term=magnesium%20bisglycinate",
    medrxiv: "https://www.medrxiv.org/search/magnesium%252Bbisglycinate",
    biorxiv: "https://www.biorxiv.org/search/magnesium%252Bbisglycinate",
    citations: [
      { title: "Bioavailability of magnesium diglycinate vs magnesium oxide in patients with ileal resection", journal: "JPEN: Journal of Parenteral & Enteral Nutrition", year: 1994, url: "https://pubmed.ncbi.nlm.nih.gov/7815675/" },
      { title: "The effects of magnesium supplementation on subjective anxiety and stress, a systematic review", journal: "Nutrients", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/28445426/" },
      { title: "Magnesium status and stress: the vicious circle concept revisited", journal: "Nutrients", year: 2020, url: "https://pubmed.ncbi.nlm.nih.gov/33260549/" },
      { title: "Effect of magnesium supplementation on primary insomnia in elderly: a double-blind placebo-controlled RCT", journal: "Journal of Research in Medical Sciences", year: 2012, url: "https://pubmed.ncbi.nlm.nih.gov/23853635/" },
    ],
  },
  {
    slug: "myo-inositol",
    name: "Myo-Inositol",
    sku: "ova",
    skuLabel: "Ova Balance",
    scientist: "Prof. Vittorio Unfer",
    affiliation: "Experts Group on Inositol in Basic & Clinical Research",
    summary:
      "Restores insulin signalling, ovulation and cycle regularity, backed by the research-backed 40:1 Myo:D-Chiro ratio.",
    studyCount: "1,500+ studies",
    pdf: "/research/myo-inositol.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-299/inositol",
    clinicalTrials: "https://clinicaltrials.gov/search?term=myo-inositol%20PCOS",
    medrxiv: "https://www.medrxiv.org/search/myo-inositol%252BPCOS",
    biorxiv: "https://www.biorxiv.org/search/myo-inositol%252BPCOS",
    citations: [
      { title: "Myo-inositol in patients with polycystic ovary syndrome: a novel method for ovulation induction", journal: "Gynecological Endocrinology", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/17952759/" },
      { title: "A comparative study between myo-inositol and metformin in the treatment of insulin-resistant women with PCOS", journal: "European Review for Medical & Pharmacological Sciences", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/28724173/" },
      { title: "Short-term effects of metformin and myo-inositol in women with PCOS: a meta-analysis", journal: "Gynecological Endocrinology", year: 2019, url: "https://pubmed.ncbi.nlm.nih.gov/30614282/" },
      { title: "Myo-inositol effects in women with PCOS: a meta-analysis of randomized controlled trials", journal: "Endocrine Connections", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/29042448/" },
    ],
  },
  {
    slug: "d-chiro-inositol",
    name: "D-Chiro Inositol",
    sku: "ova",
    skuLabel: "Ova Balance",
    scientist: "Prof. John E. Nestler",
    affiliation: "Virginia Commonwealth University",
    summary:
      "The original PCOS breakthrough, improves insulin sensitivity, androgen balance and ovulation when paired with myo-inositol in the 40:1 ratio.",
    studyCount: "400+ studies",
    pdf: "/research/d-chiro-inositol.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-1487/d-chiro-inositol",
    clinicalTrials: "https://clinicaltrials.gov/search?term=D-chiro-inositol%20PCOS",
    medrxiv: "https://www.medrxiv.org/search/D-chiro-inositol%252BPCOS",
    biorxiv: "https://www.biorxiv.org/search/D-chiro-inositol%252BPCOS",
    citations: [
      { title: "Ovulatory and metabolic effects of D-chiro-inositol in the polycystic ovary syndrome", journal: "New England Journal of Medicine", year: 1999, url: "https://pubmed.ncbi.nlm.nih.gov/10219066/" },
      { title: "Comparison of two insulin sensitizers, metformin and myo-inositol, in women with polycystic ovary syndrome", journal: "Gynecological Endocrinology", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/27808588/" },
      { title: "Randomized, double-blind placebo-controlled trial: effects of myo-inositol on ovarian function and metabolic factors in PCOS", journal: "European Review for Medical & Pharmacological Sciences", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/18074942/" },
    ],
  },
  {
    slug: "quatrefolic-l-5-mthf",
    name: "Quatrefolic® (L-5-MTHF)",
    sku: "ova",
    skuLabel: "Ova Balance",
    scientist: "Prof. Klaus Pietrzik",
    affiliation: "University of Bonn",
    summary:
      "Patented (6S)-5-methyltetrahydrofolate glucosamine salt, the active, body-ready form of folate. Bypasses the MTHFR conversion step, so it works for everyone (including the ~30-50% with C677T variants). Delivered at 400 mcg DFE per sachet.",
    studyCount: "200+ studies",
    pdf: "/research/myo-inositol.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-1017/l-methylfolate",
    clinicalTrials: "https://clinicaltrials.gov/search?term=L-5-methyltetrahydrofolate%20OR%20Quatrefolic",
    medrxiv: "https://www.medrxiv.org/search/5-methyltetrahydrofolate%252BMTHFR",
    biorxiv: "https://www.biorxiv.org/search/5-methyltetrahydrofolate%252BMTHFR",
    citations: [
      { title: "Folic acid and L-5-methyltetrahydrofolate: comparison of clinical pharmacokinetics and pharmacodynamics", journal: "Clinical Pharmacokinetics", year: 2010, url: "https://pubmed.ncbi.nlm.nih.gov/20608755/" },
      { title: "Association between serum 5-methyltetrahydrofolate and homocysteine in Chinese hypertensive participants with different MTHFR C677T genotypes", journal: "Nutrition Journal", year: 2022, url: "https://pubmed.ncbi.nlm.nih.gov/35562805/" },
      { title: "The effects of folinic acid and L-methylfolate supplementation on serum total homocysteine levels in healthy adults", journal: "Clinical Nutrition ESPEN", year: 2023, url: "https://pubmed.ncbi.nlm.nih.gov/38056998/" },
      { title: "Hyperhomocysteinemia in hypofertile male patients can be alleviated by supplementation with 5-MTHF associated with one-carbon cycle support", journal: "Frontiers in Reproductive Health", year: 2023, url: "https://pubmed.ncbi.nlm.nih.gov/37705678/" },
      { title: "5-MTHF enhances the portal pressure reduction achieved with propranolol in patients with cirrhosis: a randomized placebo-controlled trial", journal: "Journal of Hepatology", year: 2023, url: "https://pubmed.ncbi.nlm.nih.gov/37482222/" },
    ],
  },
  {

    slug: "berberine",
    name: "Berberine HCl",
    sku: "ova",
    skuLabel: "Ova Balance",
    scientist: "Prof. Jian-Dong Jiang",
    affiliation: "Chinese Academy of Medical Sciences",
    summary:
      "Plant alkaloid that helps support insulin sensitivity, lipid balance and metabolic health, delivered at the 300 mg dose used in published PCOS and metabolic trials.",
    studyCount: "800+ studies",
    pdf: "/research/berberine.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-1126/berberine",
    clinicalTrials: "https://clinicaltrials.gov/search?term=berberine%20PCOS%20insulin",
    medrxiv: "https://www.medrxiv.org/search/berberine%252BPCOS%252Binsulin",
    biorxiv: "https://www.biorxiv.org/search/berberine%252BPCOS%252Binsulin",
    citations: [
      { title: "Berberine, a natural plant product, activates AMP-activated protein kinase with beneficial metabolic effects", journal: "Diabetes", year: 2006, url: "https://pubmed.ncbi.nlm.nih.gov/16873688/" },
      { title: "Efficacy of berberine in patients with type 2 diabetes mellitus", journal: "Metabolism: Clinical & Experimental", year: 2008, url: "https://pubmed.ncbi.nlm.nih.gov/18442638/" },
      { title: "The effect of berberine on polycystic ovary syndrome patients with insulin resistance (PCOS-IR): a meta-analysis", journal: "Evidence-Based Complementary & Alternative Medicine", year: 2018, url: "https://pubmed.ncbi.nlm.nih.gov/30538756/" },
      { title: "The effect of berberine on reproduction and metabolism in women with PCOS: a systematic review", journal: "Evidence-Based Complementary & Alternative Medicine", year: 2019, url: "https://pubmed.ncbi.nlm.nih.gov/31915452/" },
    ],
  },
  {
    slug: "nac",
    name: "N-Acetyl Cysteine (NAC)",
    sku: "liver",
    skuLabel: "Liver Biome Plus",
    scientist: "Dr. Smita S. Atkuri",
    affiliation: "Stanford University",
    summary:
      "Direct precursor to glutathione, the body's master antioxidant. The clinical standard for liver protection and Phase II detox.",
    studyCount: "2,000+ studies",
    pdf: "/research/nac.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-1018/n-acetyl-cysteine-nac",
    clinicalTrials: "https://clinicaltrials.gov/search?term=N-acetylcysteine%20liver",
    medrxiv: "https://www.medrxiv.org/search/N-acetylcysteine%252Bliver",
    biorxiv: "https://www.biorxiv.org/search/N-acetylcysteine%252Bliver",
    citations: [
      { title: "N-Acetylcysteine, a safe antidote for cysteine/glutathione deficiency", journal: "Current Opinion in Pharmacology", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/17602868/" },
      { title: "N-acetylcysteine improves liver function in patients with non-alcoholic fatty liver disease", journal: "Hepatitis Monthly", year: 2010, url: "https://pubmed.ncbi.nlm.nih.gov/22308119/" },
      { title: "Efficacy of oral N-acetylcysteine in the treatment of acetaminophen overdose", journal: "New England Journal of Medicine", year: 1988, url: "https://pubmed.ncbi.nlm.nih.gov/3059186/" },
    ],
  },
  {
    slug: "silymarin",
    name: "Liposomal Milk Thistle (Silymarin)",
    sku: "liver",
    skuLabel: "Liver Biome Plus",
    scientist: "Prof. Rolf Gebhardt",
    affiliation: "University of Leipzig",
    summary:
      "Liposomal silymarin (350 mg equivalent), helps stabilise hepatocyte membranes and supports bile flow and hepatic resilience.",
    studyCount: "1,200+ studies",
    pdf: "/research/silymarin.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-138/milk-thistle",
    clinicalTrials: "https://clinicaltrials.gov/search?term=silymarin%20milk%20thistle%20liver",
    medrxiv: "https://www.medrxiv.org/search/silymarin%252Bmilk%252Bthistle%252Bliver",
    biorxiv: "https://www.biorxiv.org/search/silymarin%252Bmilk%252Bthistle%252Bliver",
    citations: [
      { title: "Milk thistle in liver diseases: past, present, future", journal: "Phytotherapy Research", year: 2010, url: "https://pubmed.ncbi.nlm.nih.gov/20564545/" },
      { title: "Silybin and the liver: from basic research to clinical practice", journal: "World Journal of Gastroenterology", year: 2011, url: "https://pubmed.ncbi.nlm.nih.gov/21633595/" },
      { title: "A phase I and pharmacokinetic study of silybin-phytosome", journal: "Investigational New Drugs", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/17077998/" },
    ],
  },
  {
    slug: "curcumin",
    name: "Liposomal Curcumin",
    sku: "liver",
    skuLabel: "Liver Biome Plus",
    scientist: "Prof. Bharat B. Aggarwal",
    affiliation: "MD Anderson Cancer Center",
    summary:
      "Phospholipid-encapsulated curcumin (100 mg), helps support a healthy inflammation response, with markedly higher plasma exposure than plain powder.",
    studyCount: "3,000+ studies",
    pdf: "/research/curcumin.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-662/turmeric",
    clinicalTrials: "https://clinicaltrials.gov/search?term=liposomal%20curcumin%20liver",
    medrxiv: "https://www.medrxiv.org/search/liposomal%252Bcurcumin%252Bliver",
    biorxiv: "https://www.biorxiv.org/search/liposomal%252Bcurcumin%252Bliver",
    citations: [
      { title: "Curcumin: the Indian solid gold", journal: "Advances in Experimental Medicine & Biology", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/17569205/" },
      { title: "Curcumin: a review of its effects on human health", journal: "Foods", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/29065496/" },
      { title: "Treatment of non-alcoholic fatty liver disease with curcumin: a randomized placebo-controlled trial", journal: "Phytotherapy Research", year: 2016, url: "https://pubmed.ncbi.nlm.nih.gov/27270872/" },
      { title: "Bioavailability of curcumin: problems and promises", journal: "Molecular Pharmaceutics", year: 2007, url: "https://pubmed.ncbi.nlm.nih.gov/17999464/" },
    ],
  },
  {
    slug: "alpha-lipoic-acid",
    name: "Liposomal Alpha-Lipoic Acid",
    sku: "liver",
    skuLabel: "Liver Biome Plus",
    scientist: "Prof. Lester Packer",
    affiliation: "UC Berkeley",
    summary:
      "The 'universal antioxidant' both water- and fat-soluble. Recycles vitamins C, E and glutathione.",
    studyCount: "600+ studies",
    pdf: "/research/alpha-lipoic-acid.pdf",
    webmd: "https://www.webmd.com/vitamins/ai/ingredientmono-767/alpha-lipoic-acid",
    clinicalTrials: "https://clinicaltrials.gov/search?term=alpha%20lipoic%20acid",
    medrxiv: "https://www.medrxiv.org/search/alpha%252Blipoic%252Bacid",
    biorxiv: "https://www.biorxiv.org/search/alpha%252Blipoic%252Bacid",
    citations: [
      { title: "Alpha-lipoic acid as a biological antioxidant", journal: "Free Radical Biology & Medicine", year: 1995, url: "https://pubmed.ncbi.nlm.nih.gov/7649494/" },
      { title: "Alpha-lipoic acid as a dietary supplement: molecular mechanisms and therapeutic potential", journal: "Biochimica et Biophysica Acta", year: 2009, url: "https://pubmed.ncbi.nlm.nih.gov/19664690/" },
      { title: "Alpha-lipoic acid (ALA) as a supplementation for weight loss: results from a meta-analysis of randomized controlled trials", journal: "Obesity Reviews", year: 2017, url: "https://pubmed.ncbi.nlm.nih.gov/28295905/" },
    ],
  },
  {
    slug: "probiotics",
    name: "Probiotic Blend (10B CFU)",
    sku: "liver",
    skuLabel: "Liver Biome Plus",
    scientist: "Prof. Mary Ellen Sanders",
    affiliation: "International Scientific Association for Probiotics & Prebiotics",
    summary:
      "Multi-strain L. acidophilus, B. lactis, L. rhamnosus, for gut barrier integrity and microbial diversity.",
    studyCount: "1,800+ studies",
    pdf: "/research/probiotics.pdf",
    webmd: "https://www.webmd.com/digestive-disorders/probiotics-overview",
    clinicalTrials: "https://clinicaltrials.gov/search?term=probiotics%20NAFLD%20gut",
    medrxiv: "https://www.medrxiv.org/search/probiotics%252BNAFLD%252Bgut",
    biorxiv: "https://www.biorxiv.org/search/probiotics%252BNAFLD%252Bgut",
    citations: [
      { title: "Expert consensus document: the ISAPP consensus statement on the scope and appropriate use of the term probiotic", journal: "Nature Reviews Gastroenterology & Hepatology", year: 2014, url: "https://pubmed.ncbi.nlm.nih.gov/24912386/" },
      { title: "Thirty years of Lactobacillus rhamnosus GG: a review", journal: "Journal of Clinical Gastroenterology", year: 2019, url: "https://pubmed.ncbi.nlm.nih.gov/30741841/" },
      { title: "Effects of probiotics on non-alcoholic fatty liver disease: a meta-analysis", journal: "World Journal of Gastroenterology", year: 2013, url: "https://pubmed.ncbi.nlm.nih.gov/24187469/" },
    ],
  },
];
